import React from 'react';
import { Box, Typography } from '@mui/material';
import { HelpOutline } from '@mui/icons-material';
import { WidgetProps } from './types';

/**
 * widget_key -> component.
 *
 * The single place a server registry key is bound to a React component.
 * Everything is `React.lazy` so a dashboard of 20 tiles ships 20 small chunks
 * rather than one bundle containing every widget in the product — the
 * difference between a dashboard that loads in under two seconds and one that
 * does not.
 *
 * Adding a widget is: write the component, add one line here, add the
 * descriptor to the server registry. When the plugin SDK arrives it registers
 * into this same map at runtime — which is why widgets receive data as props
 * and never an API client.
 */

type WidgetComponent = React.ComponentType<WidgetProps<any>>;

const L = (loader: () => Promise<{ default: WidgetComponent }>) => React.lazy(loader);

export const WIDGET_COMPONENTS: Record<string, WidgetComponent> = {
  // ── Finance ──
  'finance.cash_position': L(() => import('./widgets/finance/CashPositionWidget')),
  'finance.receivables_ageing': L(() => import('./widgets/finance/ReceivablesAgeingWidget')),
  'finance.payables_ageing': L(() => import('./widgets/finance/PayablesAgeingWidget')),
  'finance.pl_summary': L(() => import('./widgets/finance/PlSummaryWidget')),
  'finance.trial_balance_health': L(() => import('./widgets/finance/TrialBalanceHealthWidget')),
  'finance.gst_tds_compliance': L(() => import('./widgets/finance/ComplianceWidget')),

  // ── Sales ──
  'sales.revenue': L(() => import('./widgets/sales/RevenueWidget')),
  'sales.pipeline_by_stage': L(() => import('./widgets/sales/PipelineWidget')),
  'sales.win_rate': L(() => import('./widgets/sales/WinRateWidget')),
  'sales.top_customers': L(() => import('./widgets/sales/TopCustomersWidget')),
  'sales.recent_invoices': L(() => import('./widgets/sales/RecentInvoicesWidget')),
  'sales.quotations_pending': L(() => import('./widgets/sales/QuotationsPendingWidget')),
  'sales.conversion_funnel': L(() => import('./widgets/sales/ConversionFunnelWidget')),
  'sales.pipeline_value': L(() => import('./widgets/sales/PipelineValueWidget')),
  'sales.revenue_cohorts': L(() => import('./widgets/sales/RevenueCohortsWidget')),
  'sales.rep_leaderboard': L(() => import('./widgets/sales/RepLeaderboardWidget')),
  'sales.quota_gauge': L(() => import('./widgets/sales/QuotaGaugeWidget')),

  // ── Procurement ──
  'procurement.open_pos': L(() => import('./widgets/procurement/OpenPosWidget')),
  'procurement.vendor_payments_due': L(() => import('./widgets/procurement/VendorPaymentsDueWidget')),
  'procurement.purchase_spend': L(() => import('./widgets/procurement/PurchaseSpendWidget')),

  // ── Inventory ──
  'inventory.low_stock': L(() => import('./widgets/inventory/LowStockWidget')),
  'inventory.stock_value': L(() => import('./widgets/inventory/StockValueWidget')),
  'inventory.warehouse_capacity': L(() => import('./widgets/inventory/WarehouseCapacityWidget')),
  'inventory.recent_movements': L(() => import('./widgets/inventory/RecentMovementsWidget')),

  // ── Projects ──
  'projects.active_projects': L(() => import('./widgets/projects/ActiveProjectsWidget')),
  'projects.task_board': L(() => import('./widgets/projects/TaskBoardWidget')),
  'projects.overdue_tasks': L(() => import('./widgets/projects/OverdueTasksWidget')),
  'projects.budget_burn': L(() => import('./widgets/projects/BudgetBurnWidget')),

  // ── HR ──
  'hr.headcount': L(() => import('./widgets/hr/HeadcountWidget')),
  'hr.present_today': L(() => import('./widgets/hr/PresentTodayWidget')),
  'hr.pending_leave': L(() => import('./widgets/hr/PendingLeaveWidget')),
  'hr.birthdays': L(() => import('./widgets/hr/BirthdaysWidget')),
  'hr.open_positions': L(() => import('./widgets/hr/OpenPositionsWidget')),

  // ── Helpdesk ──
  'helpdesk.open_tickets': L(() => import('./widgets/helpdesk/OpenTicketsWidget')),
  'helpdesk.sla_breaches': L(() => import('./widgets/helpdesk/SlaBreachesWidget')),
  'helpdesk.avg_response': L(() => import('./widgets/helpdesk/AvgResponseWidget')),

  // ── Treasury ──
  'treasury.position': L(() => import('./widgets/treasury/TreasuryPositionWidget')),
  'treasury.cash_forecast': L(() => import('./widgets/treasury/CashForecastWidget')),
  'treasury.bank_balances': L(() => import('./widgets/treasury/BankBalancesWidget')),
  'treasury.working_capital': L(() => import('./widgets/treasury/WorkingCapitalWidget')),
  'treasury.risk_alerts': L(() => import('./widgets/treasury/RiskAlertsWidget')),

  // ── Cross-cutting ──
  'approvals.my_pending_queue': L(() => import('./widgets/approvals/MyApprovalsWidget')),
  'activity.live_feed': L(() => import('./widgets/activity/LiveFeedWidget')),
  'notifications.unread': L(() => import('./widgets/activity/NotificationsWidget')),
};

/**
 * Rendered when a saved dashboard references a key this build does not know.
 *
 * Happens whenever a widget is removed or renamed and a user still has it on a
 * board. Throwing here would take down the whole grid over one stale row, so
 * the tile degrades and says so.
 */
export const UnknownWidget: React.FC<{ widgetKey: string }> = ({ widgetKey }) => (
  <Box
    sx={{
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 1,
      p: 2,
      textAlign: 'center',
    }}
  >
    <HelpOutline sx={{ color: 'text.disabled', fontSize: 26 }} />
    <Typography variant="body2" color="text.secondary">
      This widget is no longer available
    </Typography>
    <Typography variant="caption" color="text.disabled" sx={{ fontFamily: 'monospace' }}>
      {widgetKey}
    </Typography>
  </Box>
);

export const getWidgetComponent = (key: string): WidgetComponent | null =>
  WIDGET_COMPONENTS[key] || null;

export const isKnownWidget = (key: string): boolean => key in WIDGET_COMPONENTS;
